/**
 * @file LanguageSwitcher.jsx
 * @description Globe-icon dropdown for switching the UI language (English/Spanish).
 *   Persists the choice via i18next (cookie-backed) and renders a flag + label
 *   for each available locale.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/LanguageSwitcher
 */
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { GlobeIcon } from "./Icons";
import { track, A, ctaMeta } from "../lib/analytics";

const FLAGS = {
  en: { emoji: "🇺🇸", label: "English" },
  es: { emoji: "🇪🇸", label: "Español" },
};
const LANGS = ["en", "es"];

/**
 * The one way to switch the site language (tracked, persisted in the aba_lang cookie). Used by this switcher and by
 * in-page shortcuts such as the "En español" chip on city pages.
 */
export function switchLanguage(i18n, lang) {
  const currentLang = i18n.language?.startsWith("es") ? "es" : "en";
  track(A.LANGUAGE_SWITCH_CLICK, {
    supabaseMeta: { to: lang },
    ga4Params: ctaMeta({
      pagePath: window.location.pathname,
      pageType: "language_switch",
      ctaLabel: lang === "es" ? "Español" : "English",
      targetPath: window.location.pathname,
      userLanguage: lang,
    }),
  });
  if (lang !== currentLang) {
    i18n.changeLanguage(lang);
    document.cookie = `aba_lang=${lang};max-age=31536000;path=/`;
  }
}

export default function LanguageSwitcher({ compact = false, menuRow = false, globeMenu = false, onAfterSwitch }) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("es") ? "es" : "en";
  const targetLang = currentLang === "en" ? "es" : "en";
  const target = FLAGS[targetLang];

  // Hooks must run unconditionally (used by the globeMenu variant only).
  const [globeOpen, setGlobeOpen] = useState(false);
  const globeRef = useRef(null);
  useEffect(() => {
    if (!globeOpen) return;
    const onDoc = (e) => { if (globeRef.current && !globeRef.current.contains(e.target)) setGlobeOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [globeOpen]);

  const setLang = (lang) => {
    switchLanguage(i18n, lang);
    if (onAfterSwitch) onAfterSwitch();
  };

  const handleSwitch = (e) => {
    // Prevent the event from reaching parent handlers (e.g. the profile dropdown
    // close listener that fires on any document click).
    e.stopPropagation();
    setLang(targetLang);
  };

  // ── Dropdown-menu row: a light "EN / ES" text toggle (no flags, no boxed
  //    segments). Active = highlighted; tap the other to switch. ──
  if (menuRow) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        width: "100%", padding: "9px 16px",
        fontFamily: "'Inter', 'DM Sans', sans-serif",
      }}>
        <span style={{ display: "flex", alignItems: "center", flexShrink: 0, color: T.textMid }}>
          <GlobeIcon size={15} />
        </span>
        <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid }}>
          {t("nav.language")}
        </span>
        <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 7 }}>
          {LANGS.map((lang, i) => {
            const active = lang === currentLang;
            return (
              <React.Fragment key={lang}>
                {i > 0 && <span style={{ color: T.textXLight, fontSize: 11 }}>/</span>}
                <button
                  onClick={(e) => { e.stopPropagation(); setLang(lang); }}
                  aria-pressed={active}
                  style={{
                    background: "none", border: "none", padding: 0,
                    cursor: active ? "default" : "pointer",
                    fontSize: 12, fontWeight: active ? 800 : 600,
                    color: active ? T.blue : T.textLight,
                    fontFamily: "'Inter', 'DM Sans', sans-serif",
                    letterSpacing: "0.02em",
                  }}
                >{lang.toUpperCase()}</button>
              </React.Fragment>
            );
          })}
        </span>
      </div>
    );
  }

  // ── Globe button + dropdown (guests/logged-out, who have no profile menu) ──
  if (globeMenu) {
    return (
      <div ref={globeRef} style={{ position: "relative", flexShrink: 0 }}>
        <button
          onClick={(e) => { e.stopPropagation(); setGlobeOpen(o => !o); }}
          title={t("nav.language")}
          aria-label={t("nav.language")}
          aria-expanded={globeOpen}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            width: 44, height: 44, borderRadius: 8,
            background: globeOpen ? T.blueLight : "transparent",
            border: "none", cursor: "pointer", color: globeOpen ? T.blue : T.textMid,
            transition: "all 0.15s", WebkitTapHighlightColor: "transparent",
          }}
          onMouseEnter={e => { if (!globeOpen) e.currentTarget.style.background = T.surfaceAlt; }}
          onMouseLeave={e => { if (!globeOpen) e.currentTarget.style.background = "transparent"; }}
        >
          <GlobeIcon size={18} />
        </button>
        {globeOpen && (
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0,
            background: T.surface, border: `1px solid ${T.border}`,
            borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
            minWidth: 150, zIndex: 9999, overflow: "hidden", padding: "5px 0",
          }}>
            {LANGS.map(lang => {
              const active = lang === currentLang;
              return (
                <button
                  key={lang}
                  onClick={(e) => { e.stopPropagation(); setLang(lang); setGlobeOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    width: "100%", padding: "8px 14px", textAlign: "left",
                    background: active ? T.blueLight : "transparent", border: "none", cursor: "pointer",
                    fontFamily: "'Inter', 'DM Sans', sans-serif",
                    fontSize: 13, fontWeight: active ? 700 : 500,
                    color: active ? T.blue : T.textMid,
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = T.surfaceAlt; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}
                >
                  {FLAGS[lang].label}
                  {active && <span style={{ marginLeft: "auto", color: T.blue }}>✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (compact) {
    // 44×44 minimum touch target — the emoji is centered inside an invisible
    // 44px square so thumbs hit it reliably regardless of the emoji's visual size.
    return (
      <button
        onClick={handleSwitch}
        title={target.label}
        aria-label={`Switch to ${target.label}`}
        style={{
          background: "none", border: "none", cursor: "pointer",
          minWidth: 44, minHeight: 44, padding: 0, borderRadius: 8,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, WebkitTapHighlightColor: "transparent",
        }}
      >
        <span style={{ fontSize: 22, lineHeight: 1, display: "block" }}>{target.emoji}</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleSwitch}
      aria-label={`Switch to ${target.label}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        background: "none", border: `1px solid ${T.border}`, borderRadius: 8,
        padding: "5px 10px", cursor: "pointer", fontSize: 13, fontWeight: 500,
        color: T.textMid, transition: "all 0.15s", whiteSpace: "nowrap",
        WebkitTapHighlightColor: "transparent",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.blue; e.currentTarget.style.color = T.blue; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMid; }}
    >
      <span style={{ fontSize: 16 }}>{target.emoji}</span>
      <span>{target.label}</span>
    </button>
  );
}
